import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";
import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Paper,
  Typography,
  Popover,
  Tabs,
  Tab,
  Box,
  Button,
  Chip,
  IconButton,
  Badge,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField, // ⭐ Para modal de rejeição
} from "@material-ui/core";
import {
  ClipboardList,
  Bell,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
  Info,
  Users,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import clsx from "clsx";

import { useToast } from "../Toast";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import IconButtonWithTooltip from "../ui/IconButtonWithTooltip";
import MarkdownWrapper from "../MarkdownWrapper";
import StandardModal from "../ui/StandardModal";
import api from "../../services/api";
import { getBackendUrl } from "../../config";
import { formatWithTimezone } from "../../utils/dateUtils";
import {
  addTaskActivity,
  loadTaskActivity,
  removeTaskActivity
} from "../../utils/taskActivity";

const useStyles = makeStyles((theme) => ({
  popoverPaper: {
    width: "100%",
    maxWidth: 440,
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 32px rgba(0,0,0,0.5)"
      : "0 8px 32px rgba(0,0,0,0.12)",
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
  },
  popoverHeader: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "#fff",
    padding: theme.spacing(0.875, 1.25),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  headerIcon: {
    width: 24,
    height: 24,
    borderRadius: 5,
    backgroundColor: "rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  headerContent: {
    flex: 1,
  },
  headerTitle: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "#fff",
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: "0.65rem",
    color: "rgba(255,255,255,0.65)",
    marginTop: 1,
  },
  headerActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  headerButton: {
    color: "rgba(255,255,255,0.9)",
    padding: 4,
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.1)",
    },
  },
  tabs: {
    backgroundColor: theme.palette.type === "dark"
      ? theme.palette.action.hover
      : theme.palette.background.contrast,
    borderRadius: 10,
    padding: 4,
    margin: theme.spacing(1),
    minHeight: 36,
    "& .MuiTabs-indicator": {
      display: "none",
    },
    "& .MuiTabs-flexContainer": {
      gap: 4,
    },
  },
  tab: {
    minHeight: 32,
    fontSize: "0.72rem",
    fontWeight: 600,
    textTransform: "none",
    minWidth: 0,
    flex: 1,
    padding: theme.spacing(0.5, 1),
    borderRadius: 8,
    color: theme.palette.type === "dark"
      ? theme.palette.text.secondary
      : theme.palette.action.active,
    opacity: 1,
    transition: "background-color 200ms, color 200ms",
    "&.MuiTab-textColorInherit": {
      opacity: 1,
    },
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-selected": {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      fontWeight: 700,
      opacity: 1,
    },
    "&.Mui-selected:hover": {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
    },
  },
  tabBadge: {
    marginLeft: theme.spacing(0.5),
    backgroundColor: theme.palette.error.main,
    color: "#fff",
    fontSize: "0.6rem",
    fontWeight: 700,
    padding: "1px 5px",
    borderRadius: 8,
    minWidth: 16,
    height: 16,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  mainPaper: {
    flex: 1,
    maxHeight: 360,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    border: "none",
    boxShadow: "none",
    backgroundColor: "transparent",
  },
  listItem: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 0,
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
    "&:last-child": {
      borderBottom: "none",
    },
  },
  taskItem: {
    borderLeft: "3px solid transparent",
  },
  priorityHigh: {
    borderLeftColor: theme.palette.error.main,
  },
  priorityMedium: {
    borderLeftColor: theme.palette.warning.main,
  },
  priorityLow: {
    borderLeftColor: theme.palette.info.main,
  },
  avatar: {
    width: 36,
    height: 36,
    marginRight: theme.spacing(1.25),
    fontSize: "0.8rem",
    fontWeight: 700,
  },
  taskAvatar: {
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
    color: theme.palette.primary.main,
  },
  approvalAvatar: {
    backgroundColor: alpha(theme.palette.warning.main, 0.15),
    color: theme.palette.warning.main,
  },
  infoAvatar: {
    backgroundColor: alpha(theme.palette.info.main, 0.15),
    color: theme.palette.info.main,
  },
  // ⭐ Indicador de tarefa/informativo não visto (bolinha no avatar)
  unviewedIndicator: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#22c55e",
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: "0 0 4px rgba(34, 197, 94, 0.5)",
    animation: "$pulseUnviewed 2s ease-in-out infinite",
  },
  avatarWrapper: {
    position: "relative",
    display: "inline-block",
  },
  "@keyframes pulseUnviewed": {
    "0%, 100%": {
      opacity: 1,
      transform: "scale(1)",
    },
    "50%": {
      opacity: 0.7,
      transform: "scale(1.1)",
    },
  },

  // ========================================
  // Diferenciação lido / não lido (Informativos)
  // ========================================
  unreadItem: {
    backgroundColor: theme.palette.type === "dark"
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.04),
    borderLeft: `3px solid ${theme.palette.primary.main} !important`,
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? alpha(theme.palette.primary.main, 0.14)
        : alpha(theme.palette.primary.main, 0.08),
    },
  },
  readItem: {
    opacity: 0.65,
    borderLeft: "3px solid transparent !important",
  },
  unreadTitle: {
    fontWeight: 700,
  },
  readTitle: {
    fontWeight: 400,
  },
  unreadBadge: {
    display: "inline-block",
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    marginRight: 6,
    flexShrink: 0,
    verticalAlign: "middle",
  },
  itemTitle: {
    fontSize: "0.82rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    marginBottom: 2,
    display: "-webkit-box",
    WebkitLineClamp: 1,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  itemDescription: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  itemMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  timestamp: {
    fontSize: "0.65rem",
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(0.25),
  },
  statusChip: {
    height: 18,
    fontSize: "0.65rem",
    fontWeight: 600,
  },
  statusPending: {
    backgroundColor: alpha("#9e9e9e", 0.15),
    color: "#616161",
  },
  statusInProgress: {
    backgroundColor: alpha("#2196f3", 0.15),
    color: "#1565c0",
  },
  dueDateText: {
    fontSize: "0.65rem",
    color: theme.palette.text.disabled,
    display: "flex",
    alignItems: "center",
    gap: 3,
  },
  dueDateOverdue: {
    color: theme.palette.error.main,
    fontWeight: 600,
  },
  dueDateToday: {
    color: theme.palette.warning.main,
    fontWeight: 600,
  },
  approvalActions: {
    display: "flex",
    justifyContent: "flex-start",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  approveButton: {
    height: 28,
    minWidth: "auto",
    padding: theme.spacing(0.5, 1.5),
    fontSize: "0.72rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 14,
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    color: theme.palette.success.main,
    border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
    "&:hover": {
      backgroundColor: alpha(theme.palette.success.main, 0.2),
    },
  },
  rejectButton: {
    height: 28,
    minWidth: "auto",
    padding: theme.spacing(0.5, 1.5),
    fontSize: "0.72rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 14,
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    color: theme.palette.error.main,
    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, 0.2),
    },
  },
  approvalStatus: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    backgroundColor: alpha(theme.palette.warning.main, 0.08),
    borderRadius: 6,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
  },
  approvalStatusText: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    display: "flex",
    alignItems: "flex-start",
    gap: 4,
    "&:not(:last-child)": {
      marginBottom: 4,
    },
  },
  emptyState: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
  },
  emptyIcon: {
    width: 48,
    height: 48,
    margin: "0 auto 12px",
    opacity: 0.3,
    color: theme.palette.text.disabled,
  },
  emptyText: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  footer: {
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "center",
    backgroundColor: theme.palette.type === "dark"
      ? alpha(theme.palette.background.contrast || theme.palette.background.default, 0.9)
      : theme.palette.background.default,
  },
  viewAllButton: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "none",
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  // Animação de spin para o refresh
  spinAnimation: {
    animation: "$spin 1s linear infinite",
  },
  "@keyframes spin": {
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
  },
  // Animação de pulse para o ícone
  priorityPulseBase: {
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
    animationFillMode: "both",
  },
  priorityPulseHigh: {
    animationName: "$pulseHigh",
    animationDuration: "0.9s", // Mais rápido = mais urgente
    border: "2px solid #f44336 !important",
    backgroundColor: "rgba(244, 67, 54, 0.2) !important",
    borderRadius: "50%",
    "& svg": {
      color: "#f44336 !important",
    },
  },
  priorityPulseMedium: {
    animationName: "$pulseMedium",
    animationDuration: "1.1s",
    border: "2px solid #ffb300 !important",
    backgroundColor: "rgba(255, 179, 0, 0.18) !important",
    borderRadius: "50%",
    "& svg": {
      color: "#ffb300 !important",
    },
  },
  priorityPulseLow: {
    animationName: "$pulseLow",
    animationDuration: "1.3s",
    border: "2px solid #4caf50 !important",
    backgroundColor: "rgba(76, 175, 80, 0.18) !important",
    borderRadius: "50%",
    "& svg": {
      color: "#4caf50 !important",
    },
  },
  "@keyframes pulseHigh": {
    "0%": { 
      boxShadow: "0 0 0 0 rgba(244, 67, 54, 0.7)",
      transform: "scale(1)",
    },
    "50%": { 
      boxShadow: "0 0 0 4px rgba(244, 67, 54, 0.3)",
      transform: "scale(1.03)",
    },
    "100%": { 
      boxShadow: "0 0 0 0 rgba(244, 67, 54, 0)",
      transform: "scale(1)",
    },
  },
  "@keyframes pulseMedium": {
    "0%": { 
      boxShadow: "0 0 0 0 rgba(255, 179, 0, 0.6)",
      transform: "scale(1)",
    },
    "50%": { 
      boxShadow: "0 0 0 4px rgba(255, 179, 0, 0.25)",
      transform: "scale(1.02)",
    },
    "100%": { 
      boxShadow: "0 0 0 0 rgba(255, 179, 0, 0)",
      transform: "scale(1)",
    },
  },
  "@keyframes pulseLow": {
    "0%": { 
      boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.5)",
      transform: "scale(1)",
    },
    "50%": { 
      boxShadow: "0 0 0 3px rgba(76, 175, 80, 0.2)",
      transform: "scale(1.01)",
    },
    "100%": { 
      boxShadow: "0 0 0 0 rgba(76, 175, 80, 0)",
      transform: "scale(1)",
    },
  },
}));

// Componente TabPanel
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`unified-tabpanel-${index}`}
      aria-labelledby={`unified-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

// Componente Dialog para Announcement - usando StandardModal
function AnnouncementDialog({ announcement, open, onClose }) {
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    return `${getBackendUrl()}/public/${path}`;
  };

  if (!announcement) return null;

  return (
    <StandardModal
      open={open}
      onClose={onClose}
      title={announcement.title || 'Anúncio'}
      titleIcon={<Info size={18} />}
      maxWidth="sm"
      primaryButton={{
        text: 'Entendi',
        onClick: onClose,
      }}
      secondaryButton={{ visible: false }}
    >
      {announcement.mediaPath && (
        <div style={{ 
          width: '100%', 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: 16,
          borderRadius: 12,
          overflow: 'hidden',
        }}>
          <img
            alt="announcement"
            src={getImageUrl(announcement.mediaPath)}
            style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 8 }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
      )}
      <div style={{ fontSize: '0.95rem', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        <MarkdownWrapper>{announcement.text}</MarkdownWrapper>
      </div>
    </StandardModal>
  );
}

export default function TasksPopover() {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const appToast = useToast();

  const [anchorEl, setAnchorEl] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [processingApproval, setProcessingApproval] = useState(null);
  const [activityNotifications, setActivityNotifications] = useState([]);
  const [unviewedTasksCount, setUnviewedTasksCount] = useState(0); // ⭐ Tarefas não vistas
  const [unviewedTaskIds, setUnviewedTaskIds] = useState(new Set()); // ⭐ IDs das tarefas não vistas
  
  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [unviewedAnnouncementsCount, setUnviewedAnnouncementsCount] = useState(0); // ⭐ Informativos não vistos
  const [unviewedAnnouncementIds, setUnviewedAnnouncementIds] = useState(new Set()); // ⭐ IDs dos informativos não vistos

  // ⭐ Estado para modal de rejeição
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [selectedApprovalForRejection, setSelectedApprovalForRejection] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // ========================================
  // FETCH DATA
  // ========================================

  const fetchMyTasks = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks", {
        params: {
          status: "pending,in_progress",
          limit: 10,
          sortBy: "createdAt", // ⭐ Tarefas mais recentes primeiro
          sortOrder: "DESC",
        },
      });
      setTasks(data.tasks || []);
    } catch (err) {
      console.error("Erro ao buscar tarefas:", err);
    }
  }, []);

  const fetchPendingApprovals = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks/approvals/pending");
      setPendingApprovals(data || []);
    } catch (err) {
      console.error("Erro ao buscar aprovações:", err);
    }
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { pageNumber: 1 },
      });
      setAnnouncements(data.records || []);
    } catch (err) {
      console.error("Erro ao buscar informativos:", err);
    }
  }, []);

  // ⭐ Buscar contagem e IDs de informativos não visualizados
  const fetchUnviewedAnnouncements = useCallback(async () => {
    try {
      const { data } = await api.get("/announcements/unviewed");
      setUnviewedAnnouncementsCount(data.count || 0);
      setUnviewedAnnouncementIds(new Set(data.announcementIds || []));
    } catch (err) {
      console.error("Erro ao buscar informativos não vistos:", err);
    }
  }, []);

  // ⭐ Marcar UM informativo específico como visto (atualização otimista)
  const markAnnouncementAsViewed = useCallback(async (announcementId) => {
    // Verifica se o informativo ainda não foi visto
    if (!unviewedAnnouncementIds.has(announcementId)) return;
    
    // ✅ Atualização otimista - atualiza UI imediatamente
    setUnviewedAnnouncementIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(announcementId);
      return newSet;
    });
    setUnviewedAnnouncementsCount((prev) => Math.max(0, prev - 1));
    
    try {
      await api.post(`/announcements/${announcementId}/view`);
    } catch (err) {
      // ❌ Rollback em caso de erro
      console.error("Erro ao marcar informativo como visto:", err);
      setUnviewedAnnouncementIds((prev) => new Set([...prev, announcementId]));
      setUnviewedAnnouncementsCount((prev) => prev + 1);
    }
  }, [unviewedAnnouncementIds]);

  // ⭐ Buscar contagem e IDs de tarefas não vistas
  const fetchUnviewedCount = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks/unviewed-count");
      setUnviewedTasksCount(data.count || 0);
      setUnviewedTaskIds(new Set(data.taskIds || []));
    } catch (err) {
      console.error("Erro ao buscar tarefas não vistas:", err);
    }
  }, []);

  // ⭐ Marcar UMA tarefa específica como vista
  const markTaskAsViewed = useCallback(async (taskId) => {
    // Verifica se a tarefa ainda não foi vista
    if (!unviewedTaskIds.has(taskId)) return;
    
    // ✅ Atualização otimista - atualiza UI imediatamente
    setUnviewedTaskIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(taskId);
      return newSet;
    });
    setUnviewedTasksCount((prev) => Math.max(0, prev - 1));
    
    try {
      await api.post(`/tasks/${taskId}/viewed`);
    } catch (err) {
      console.error("Erro ao marcar tarefa como vista:", err);
      // ✅ Rollback em caso de erro - restaura estado
      setUnviewedTaskIds((prev) => new Set([...prev, taskId]));
      setUnviewedTasksCount((prev) => prev + 1);
    }
  }, [unviewedTaskIds]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchMyTasks(), 
      fetchPendingApprovals(), 
      fetchAnnouncements(), 
      fetchUnviewedCount(),
      fetchUnviewedAnnouncements() // ⭐ Informativos não vistos
    ]);
    setLoading(false);
  }, [fetchMyTasks, fetchPendingApprovals, fetchAnnouncements, fetchUnviewedCount, fetchUnviewedAnnouncements]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Carregar notificações persistidas do usuário (unread)
  useEffect(() => {
    if (!user?.id) return;
    setActivityNotifications(loadTaskActivity(user.id));
  }, [user?.id]);

  // ========================================
  // SOCKET.IO LISTENERS
  // ========================================

  useEffect(() => {
    if (!user?.companyId) return;
    if (!socket) return;

    // Task handlers
    const handleTaskUpdate = (data) => {
      if (data.action === "update" || data.action === "status_changed") {
        fetchMyTasks();
      }
      if (data.action === "create" || data.action === "delete") {
        fetchMyTasks();
        fetchUnviewedCount(); // ⭐ Atualizar contagem de tarefas não vistas
      }
      if (data.action === "approval_requested" || data.action === "approval_partial") {
        fetchPendingApprovals();
      }
    };

    const handleTaskNotification = (data) => {
      if (data.action === "assigned" || data.action === "unassigned" || data.action === "deleted") {
        fetchMyTasks();
        fetchUnviewedCount(); // ⭐ Atualizar contagem quando atribuído/desatribuído/excluído
      }
      if (data.action === "task_modified") {
        const notif = {
          id: data.id || `${data.kind || "task_modified"}:${data.taskId}:${data.createdAt || Date.now()}`,
          taskId: data.taskId,
          taskTitle: data.taskTitle,
          ticketId: data.ticketId,
          ticketUuid: data.ticketUuid,
          kind: data.kind,
          actorName: data.actorName,
          preview: data.preview,
          createdAt: data.createdAt || new Date().toISOString()
        };
        const next = addTaskActivity(user.id, notif);
        setActivityNotifications(next);
      }
    };

    const handleTaskApproval = (data) => {
      if (data.action === "approval_request") {
        fetchPendingApprovals();
      }
      if (data.action === "request_rejected") {
        fetchPendingApprovals();
        // ⭐ Notificação visual destacada quando VOCÊ solicitou e foi rejeitado
        const rejectorName = data.rejectedByName || "Um participante";
        const taskTitle = data.taskTitle || "Tarefa";
        const reason = data.rejectionReason;
        
        // Toast mais destacado com detalhes
        if (reason) {
          appToast.error(
            `❌ ${rejectorName} rejeitou sua solicitação para "${taskTitle}". Motivo: "${reason}"`,
            { autoHideDuration: 8000 }
          );
        } else {
          appToast.warning(
            `⚠️ ${rejectorName} rejeitou sua solicitação de mudança de status para "${taskTitle}"`,
            { autoHideDuration: 6000 }
          );
        }
      }
    };

    // Announcement handlers
    const handleAnnouncement = (data) => {
      if (data.action === "update" || data.action === "create") {
        setAnnouncements(prev => {
          const idx = prev.findIndex(a => a.id === data.record.id);
          if (idx !== -1) {
            const updated = [...prev];
            updated[idx] = data.record;
            return updated;
          }
          return [data.record, ...prev];
        });
        
        // ⭐ Novo informativo criado OU ativado = adicionar aos não visualizados
        if (data.record?.status) {
          setUnviewedAnnouncementIds(prev => {
            // Só adiciona se ainda não existir (evita duplicatas)
            if (prev.has(data.record.id)) return prev;
            const newSet = new Set(prev);
            newSet.add(data.record.id);
            return newSet;
          });
          // Recalcula a contagem baseada no Set atualizado
          setUnviewedAnnouncementsCount(prev => {
            // Incrementa apenas para CREATE, não para UPDATE
            if (data.action === "create") return prev + 1;
            return prev;
          });
        }
      }
      if (data.action === "delete") {
        const deletedId = +data.id;
        setAnnouncements(prev => prev.filter(a => a.id !== deletedId));
        setUnviewedAnnouncementIds(prev => {
          if (!prev.has(deletedId)) return prev;
          const newSet = new Set(prev);
          newSet.delete(deletedId);
          return newSet;
        });
        setUnviewedAnnouncementsCount(c => Math.max(0, c - 1));
      }
    };

    socket.on(`company-${user.companyId}-task`, handleTaskUpdate);
    socket.on(`user-${user.id}-task-notification`, handleTaskNotification);
    socket.on(`user-${user.id}-task-approval`, handleTaskApproval);
    socket.on(`company-announcement`, handleAnnouncement);
    socket.on(`user-${user.id}-announcement`, handleAnnouncement);

    return () => {
      if (socket) {
        socket.off(`company-${user.companyId}-task`, handleTaskUpdate);
        socket.off(`user-${user.id}-task-notification`, handleTaskNotification);
        socket.off(`user-${user.id}-task-approval`, handleTaskApproval);
        socket.off(`company-announcement`, handleAnnouncement);
        socket.off(`user-${user.id}-announcement`, handleAnnouncement);
      }
    };
  }, [user?.id, user?.companyId, socket, fetchMyTasks, fetchPendingApprovals, fetchUnviewedCount, fetchUnviewedAnnouncements, appToast]);

  // ========================================
  // HANDLERS
  // ========================================

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // ⭐ NÃO marca automaticamente - só marca quando clicar na tarefa específica
  };

  const handleViewAll = () => {
    handleClose();
    history.push("/tasks");
  };

  const handleTaskClick = (task) => {
    // ⭐ Marca a tarefa como vista antes de navegar
    markTaskAsViewed(task.id);
    handleClose();
    history.push(`/tasks?taskId=${task.id}`);
  };

  const handleActivityClick = (notif) => {
    if (!user?.id) return;
    const next = removeTaskActivity(user.id, notif.id);
    setActivityNotifications(next);
    handleClose();

    // Ir para o ticket da tarefa, se existir
    if (notif.ticketId) {
      history.push(`/tickets/${notif.ticketId}`);
      return;
    }
    history.push(`/tasks?taskId=${notif.taskId}`);
  };

  const handleApprove = async (approval) => {
    try {
      setProcessingApproval(approval.id);
      await api.post(`/tasks/approvals/${approval.id}/respond`, { approved: true });
      appToast.success(i18n.t("tasks.popover.approval.approved"));
      fetchPendingApprovals();
      fetchMyTasks();
    } catch (err) {
      console.error("Erro ao aprovar:", err);
      appToast.error(i18n.t("tasks.popover.approval.error"));
    } finally {
      setProcessingApproval(null);
    }
  };

  // ⭐ Abre o modal para informar motivo da rejeição
  const handleReject = (approval) => {
    setSelectedApprovalForRejection(approval);
    setRejectionReason("");
    setRejectionDialogOpen(true);
  };

  // ⭐ Confirma a rejeição com o motivo
  const handleConfirmReject = async () => {
    if (!selectedApprovalForRejection) return;

    try {
      setProcessingApproval(selectedApprovalForRejection.id);
      await api.post(`/tasks/approvals/${selectedApprovalForRejection.id}/respond`, { 
        approved: false,
        rejectionReason: rejectionReason.trim() || null
      });
      appToast.info(i18n.t("tasks.popover.approval.rejected"));
      fetchPendingApprovals();
      setRejectionDialogOpen(false);
      setSelectedApprovalForRejection(null);
      setRejectionReason("");
    } catch (err) {
      console.error("Erro ao rejeitar:", err);
      appToast.error(i18n.t("tasks.popover.approval.error"));
    } finally {
      setProcessingApproval(null);
    }
  };

  // ⭐ Cancela a rejeição
  const handleCancelReject = () => {
    setRejectionDialogOpen(false);
    setSelectedApprovalForRejection(null);
    setRejectionReason("");
  };

  const handleAnnouncementClick = async (announcement) => {
    // ⭐ Marca o informativo como visto (atualização otimista com decremento do badge)
    markAnnouncementAsViewed(announcement.id);
    
    try {
      const { data } = await api.get(`/announcements/${announcement.id}`);
      setSelectedAnnouncement(data);
      setAnnouncementDialogOpen(true);
    } catch (err) {
      console.error("Erro ao buscar detalhes do anúncio:", err);
    }
  };

  const handleAnnouncementDialogClose = () => {
    setAnnouncementDialogOpen(false);
    setSelectedAnnouncement(null);
  };

  // ========================================
  // HELPERS
  // ========================================

  const getPriorityClass = (priority) => {
    if (priority === "high") return classes.priorityHigh;
    if (priority === "medium") return classes.priorityMedium;
    return classes.priorityLow;
  };

  const getStatusLabel = useCallback((status) => {
    return i18n.t(`tasks.status.${status}`) || status;
  }, []);

  const formatDueDate = useCallback((dueDate) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    const now = new Date();
    
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const overdue = dateOnly < todayOnly;
    const today = dateOnly.getTime() === todayOnly.getTime();

    return {
      text: formatDistanceToNow(date, { addSuffix: true, locale: ptBR }),
      overdue,
      today,
    };
  }, []);

  const getInitials = (name) => {
    if (!name) return "?";
    const clean = name.replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, "").trim();
    if (!clean) return name.charAt(0) || "?";
    return clean
      .split(" ")
      .filter(Boolean)
      .map((word) => word[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }
    return `${getBackendUrl()}/public/${path}`;
  };

  // ========================================
  // COMPUTED VALUES
  // ========================================

  const open = Boolean(anchorEl);
  const id = open ? "unified-popover" : undefined;

  const { totalPending, totalInProgress, totalApprovals } = useMemo(() => ({
    totalPending: tasks.filter((t) => t.status === "pending").length,
    totalInProgress: tasks.filter((t) => t.status === "in_progress").length,
    totalApprovals: pendingApprovals.length,
  }), [tasks, pendingApprovals]);

  const totalActivity = activityNotifications.length;
  // ⭐ Badge agora usa contagem de informativos NÃO VISTOS (decremental)
  const totalBadge = totalApprovals + unviewedAnnouncementsCount + totalActivity + unviewedTasksCount;

  // Pulsar SEMPRE que houver tarefas pendentes, aprovações ou anúncios para "incomodar" o usuário
  const shouldPulse = useMemo(() => {
    // ⭐ Tarefas não vistas - prioridade alta
    if (unviewedTasksCount > 0) return true;
    // Aprovações pendentes - máxima prioridade
    if (totalApprovals > 0) return true;
    // ⭐ Informativos não visualizados
    if (unviewedAnnouncementsCount > 0) return true;
    // Atividade (unread)
    if (totalActivity > 0) return true;
    // Tarefas vencidas ou vencendo hoje
    return tasks.some(t => {
      if (!t.dueDate) return false;
      const dueInfo = formatDueDate(t.dueDate);
      return dueInfo?.overdue || dueInfo?.today;
    });
  }, [tasks, totalApprovals, unviewedAnnouncementsCount, totalActivity, unviewedTasksCount, formatDueDate]);

  const highestPriority = useMemo(() => {
    // ⭐ Tarefas não vistas = prioridade média (amarelo)
    if (unviewedTasksCount > 0) return "medium";
    if (totalApprovals > 0) return "high";
    // Verificar prioridade dos anúncios (1=alta, 2=média, 3=baixa)
    const highPriorityAnn = announcements.find(a => a.priority === 1);
    if (highPriorityAnn) return "high";
    const mediumPriorityAnn = announcements.find(a => a.priority === 2);
    if (mediumPriorityAnn) return "medium";
    const urgentTask = tasks.find(t => t.priority === "high");
    if (urgentTask) return "high";
    const mediumTask = tasks.find(t => t.priority === "medium");
    if (mediumTask) return "medium";
    return "low";
  }, [tasks, announcements, totalApprovals, unviewedTasksCount]);

  const pulseClass = shouldPulse
    ? clsx(
        classes.priorityPulseBase,
        highestPriority === "high"
          ? classes.priorityPulseHigh
          : highestPriority === "medium"
          ? classes.priorityPulseMedium
          : classes.priorityPulseLow
      )
    : undefined;

  const iconColor = shouldPulse
    ? highestPriority === "high"
      ? "#f44336"
      : highestPriority === "medium"
      ? "#ffb300"
      : "#4caf50"
    : undefined;

  // ========================================
  // RENDER
  // ========================================

  return (
    <div>
      <AnnouncementDialog
        announcement={selectedAnnouncement}
        open={announcementDialogOpen}
        onClose={handleAnnouncementDialogClose}
      />

      {/* ⭐ Modal de Motivo da Rejeição */}
      <Dialog
        open={rejectionDialogOpen}
        onClose={handleCancelReject}
        maxWidth="sm"
        fullWidth
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{ paddingBottom: 8, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <XCircle size={20} color="#ef4444" />
            <Typography variant="h6" style={{ fontSize: "1rem", fontWeight: 600 }}>
              Rejeitar Solicitação
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          {selectedApprovalForRejection && (
            <Box mb={2}>
              <Typography variant="body2" color="textSecondary">
                Você está rejeitando a mudança de status da tarefa{" "}
                <strong>"{selectedApprovalForRejection.task?.title}"</strong>{" "}
                de <Chip size="small" label={getStatusLabel(selectedApprovalForRejection.fromStatus)} />{" "}
                para <Chip size="small" label={getStatusLabel(selectedApprovalForRejection.toStatus)} />.
              </Typography>
            </Box>
          )}
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            label="Motivo da rejeição (opcional)"
            placeholder="Explique por que você está rejeitando esta solicitação..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button 
            onClick={handleCancelReject}
            disabled={processingApproval}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmReject}
            variant="contained"
            color="secondary"
            disabled={processingApproval}
            startIcon={processingApproval ? <CircularProgress size={16} color="inherit" /> : <XCircle size={16} />}
          >
            {processingApproval ? "Rejeitando..." : "Confirmar Rejeição"}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Badge
        badgeContent={totalBadge}
        color="error"
        invisible={totalBadge === 0}
        overlap="circular"
      >
        <IconButtonWithTooltip
          icon={ClipboardList}
          tooltip={i18n.t("tasks.popover.title") || "Central de Atividades"}
          onClick={handleClick}
          aria-label="Central de Atividades"
          className={pulseClass}
          iconColor={iconColor}
        />
      </Badge>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        classes={{ paper: classes.popoverPaper }}
      >
        {/* Header */}
        <div className={classes.popoverHeader}>
          <div className={classes.headerIcon}>
            <ClipboardList size={16} color="#fff" />
          </div>
          <div className={classes.headerContent}>
            <Typography className={classes.headerTitle}>
              {i18n.t("tasks.popover.title") || "Central de Atividades"}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {totalPending} {i18n.t("tasks.popover.pending")} • {totalInProgress} {i18n.t("tasks.popover.inProgress")}
            </Typography>
          </div>
          <div className={classes.headerActions}>
            <IconButton
              className={classes.headerButton}
              size="small"
              onClick={fetchAll}
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? classes.spinAnimation : ""} />
            </IconButton>
          </div>
        </div>

        {/* Tabs - 3 abas */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          className={classes.tabs}
          variant="fullWidth"
        >
          <Tab
            className={classes.tab}
            label={
              <Box display="flex" alignItems="center">
                <ClipboardList size={13} style={{ marginRight: 3 }} />
                {i18n.t("tasks.popover.tabs.tasks")}
                {unviewedTasksCount > 0 && (
                  <span className={classes.tabBadge}>{unviewedTasksCount}</span>
                )}
              </Box>
            }
          />
          <Tab
            className={classes.tab}
            label={
              <Box display="flex" alignItems="center">
                <Bell size={13} style={{ marginRight: 3 }} />
                {i18n.t("tasks.popover.tabs.notifications")}
                {totalApprovals > 0 && (
                  <span className={classes.tabBadge}>{totalApprovals}</span>
                )}
              </Box>
            }
          />
          <Tab
            className={classes.tab}
            label={
              <Box display="flex" alignItems="center">
                <Info size={13} style={{ marginRight: 3 }} />
                {i18n.t("tasks.popover.tabs.announcements") || "Informativos"}
                {unviewedAnnouncementsCount > 0 && (
                  <span className={classes.tabBadge}>{unviewedAnnouncementsCount}</span>
                )}
              </Box>
            }
          />
        </Tabs>

        {/* Tab 0: Minhas Tarefas */}
        <TabPanel value={tabValue} index={0}>
          <Paper className={classes.mainPaper}>
            {loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={28} />
              </div>
            ) : tasks.length > 0 ? (
              <List disablePadding dense>
                {tasks.map((task) => {
                  const dueInfo = task.dueDate ? formatDueDate(task.dueDate) : null;
                  const isUnviewed = unviewedTaskIds.has(task.id); // ⭐ Verifica se não foi vista
                  return (
                    <ListItem
                      key={task.id}
                      className={clsx(
                        classes.listItem,
                        classes.taskItem,
                        getPriorityClass(task.priority)
                      )}
                      button
                      onClick={() => handleTaskClick(task)}
                    >
                      <ListItemAvatar>
                        <div className={classes.avatarWrapper}>
                          <Avatar className={clsx(classes.avatar, classes.taskAvatar)}>
                            {getInitials(task.title)}
                          </Avatar>
                          {/* ⭐ Indicador de não vista */}
                          {isUnviewed && <span className={classes.unviewedIndicator} />}
                        </div>
                      </ListItemAvatar>
                      <ListItemText
                        disableTypography
                        primary={
                          <Typography className={classes.itemTitle}>
                            {task.title}
                          </Typography>
                        }
                        secondary={
                          <>
                            {task.description && (
                              <Typography className={classes.itemDescription}>
                                {task.description}
                              </Typography>
                            )}
                            <div className={classes.itemMeta}>
                              <Chip
                                size="small"
                                label={getStatusLabel(task.status)}
                                className={clsx(
                                  classes.statusChip,
                                  task.status === "pending"
                                    ? classes.statusPending
                                    : classes.statusInProgress
                                )}
                              />
                              {dueInfo && (
                                <Typography
                                  className={clsx(
                                    classes.dueDateText,
                                    dueInfo.overdue && classes.dueDateOverdue,
                                    dueInfo.today && classes.dueDateToday
                                  )}
                                >
                                  <Clock size={10} />
                                  {dueInfo.text}
                                </Typography>
                              )}
                            </div>
                          </>
                        }
                      />
                      <ArrowRight size={16} color="#999" />
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <div className={classes.emptyState}>
                <CheckCircle2 className={classes.emptyIcon} />
                <Typography className={classes.emptyText}>
                  {i18n.t("tasks.popover.emptyTasks")}
                </Typography>
              </div>
            )}
          </Paper>
        </TabPanel>

        {/* Tab 1: Notificações (Atividade + Aprovações) */}
        <TabPanel value={tabValue} index={1}>
          <Paper className={classes.mainPaper}>
            {loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={28} />
              </div>
            ) : activityNotifications.length > 0 || pendingApprovals.length > 0 ? (
              <List disablePadding dense>
                {activityNotifications.map((n) => (
                  <ListItem
                    key={n.id}
                    className={classes.listItem}
                    button
                    onClick={() => handleActivityClick(n)}
                  >
                    <ListItemAvatar>
                      <Avatar className={clsx(classes.avatar, classes.approvalAvatar)}>
                        <Bell size={18} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography className={classes.itemTitle}>
                          {n.ticketUuid ? `Ticket ${n.ticketUuid}` : "Tarefa modificada"}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography className={classes.itemDescription}>
                            <strong>{n.taskTitle || "Tarefa"}</strong>
                          </Typography>
                          <Typography className={classes.itemDescription}>
                            {`Ticket ${n.ticketUuid || "—"} modificado: ${
                              n.kind === "observation_created"
                                ? "Adicionada observação"
                                : n.kind === "observation_updated"
                                ? "Observação atualizada"
                                : "Observação removida"
                            } por: ${n.actorName || "Usuário"}`}
                          </Typography>
                          {n.preview ? (
                            <Typography className={classes.itemDescription} style={{ opacity: 0.85 }}>
                              {`"${n.preview}"`}
                            </Typography>
                          ) : null}
                        </>
                      }
                    />
                    <ArrowRight size={16} color="#999" />
                  </ListItem>
                ))}

                {activityNotifications.length > 0 && pendingApprovals.length > 0 && (
                  <Box style={{ padding: "6px 12px" }}>
                    <Divider />
                  </Box>
                )}

                {pendingApprovals.map((approval) => (
                  <ListItem
                    key={approval.id}
                    className={classes.listItem}
                  >
                    <ListItemAvatar>
                      <Avatar className={clsx(classes.avatar, classes.approvalAvatar)}>
                        <AlertTriangle size={18} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      disableTypography
                      primary={
                        <Typography className={classes.itemTitle}>
                          {i18n.t("tasks.popover.approval.title")}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography className={classes.itemDescription}>
                            <strong>{approval.requestedBy?.name || i18n.t("tasks.popover.approval.someone")}</strong>{" "}
                            {i18n.t("tasks.popover.approval.wantsToChange")} "{approval.task?.title}"{" "}
                            {i18n.t("tasks.popover.approval.from")}{" "}
                            <strong>{getStatusLabel(approval.fromStatus)}</strong>{" "}
                            {i18n.t("tasks.popover.approval.to")}{" "}
                            <strong>{getStatusLabel(approval.toStatus)}</strong>
                          </Typography>
                          
                          {/* Mostrar quem falta aprovar */}
                          {approval.pendingUsers && approval.pendingUsers.length > 0 && (
                            <div className={classes.approvalStatus}>
                              <Typography className={classes.approvalStatusText}>
                                <Users size={12} style={{ marginRight: 4, verticalAlign: "middle" }} />
                                <span style={{ color: "#ff9800" }}>
                                  Aguardando ({approval.totalPending || 0}):
                                </span>{" "}
                                {approval.pendingUsers.map(u => u.name).join(", ")}
                              </Typography>
                              {approval.approvedUsers && approval.approvedUsers.length > 0 && (
                                <Typography className={classes.approvalStatusText}>
                                  <CheckCircle2 size={12} style={{ marginRight: 4, verticalAlign: "middle", color: "#4caf50" }} />
                                  <span style={{ color: "#4caf50" }}>
                                    Aprovaram ({approval.totalApproved || 0}):
                                  </span>{" "}
                                  {approval.approvedUsers.map(u => u.name).join(", ")}
                                </Typography>
                              )}
                            </div>
                          )}
                          
                          <div className={classes.approvalActions}>
                            <Button
                              variant="contained"
                              className={classes.approveButton}
                              onClick={() => handleApprove(approval)}
                              disabled={processingApproval === approval.id}
                              startIcon={<CheckCircle2 size={14} />}
                            >
                              {i18n.t("tasks.popover.approval.approve")}
                            </Button>
                            <Button
                              variant="contained"
                              className={classes.rejectButton}
                              onClick={() => handleReject(approval)}
                              disabled={processingApproval === approval.id}
                              startIcon={<XCircle size={14} />}
                            >
                              {i18n.t("tasks.popover.approval.reject")}
                            </Button>
                          </div>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <div className={classes.emptyState}>
                <Bell className={classes.emptyIcon} />
                <Typography className={classes.emptyText}>
                  {i18n.t("tasks.popover.emptyNotifications")}
                </Typography>
              </div>
            )}
          </Paper>
        </TabPanel>

        {/* Tab 2: Informativos (Announcements) */}
        <TabPanel value={tabValue} index={2}>
          <Paper className={classes.mainPaper}>
            {loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={28} />
              </div>
            ) : announcements.length > 0 ? (
              <List disablePadding dense>
                {announcements.map((announcement) => {
                  const isUnread = unviewedAnnouncementIds.has(announcement.id);
                  return (
                    <ListItem
                      key={announcement.id}
                      className={clsx(
                        classes.listItem,
                        classes.taskItem,
                        isUnread ? classes.unreadItem : classes.readItem,
                      )}
                      button
                      onClick={() => handleAnnouncementClick(announcement)}
                    >
                      <ListItemAvatar>
                        <div className={classes.avatarWrapper}>
                          {announcement.mediaPath ? (
                            <Avatar
                              className={classes.avatar}
                              src={getImageUrl(announcement.mediaPath)}
                            />
                          ) : (
                            <Avatar className={clsx(classes.avatar, classes.infoAvatar)}>
                              {getInitials(announcement.title)}
                            </Avatar>
                          )}
                          {isUnread && <span className={classes.unviewedIndicator} />}
                        </div>
                      </ListItemAvatar>
                      <ListItemText
                        disableTypography
                        primary={
                          <Typography
                            className={clsx(
                              classes.itemTitle,
                              isUnread ? classes.unreadTitle : classes.readTitle,
                            )}
                          >
                            {isUnread && <span className={classes.unreadBadge} />}
                            {announcement.title}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography className={classes.itemDescription}>
                              {announcement.text?.replace(/[#*]/g, "").substring(0, 80)}
                              {announcement.text?.length > 80 && "..."}
                            </Typography>
                            <Typography className={classes.timestamp}>
                              {formatWithTimezone(announcement.createdAt, "dd/MM HH:mm")}
                            </Typography>
                          </>
                        }
                      />
                      <ArrowRight size={16} color={isUnread ? undefined : "#999"} />
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <div className={classes.emptyState}>
                <Info className={classes.emptyIcon} />
                <Typography className={classes.emptyText}>
                  {i18n.t("announcements.notRegister") || "Nenhum informativo"}
                </Typography>
              </div>
            )}
          </Paper>
        </TabPanel>

        {/* Footer */}
        <div className={classes.footer}>
          <Button
            className={classes.viewAllButton}
            onClick={handleViewAll}
            endIcon={<ArrowRight size={14} />}
          >
            {i18n.t("tasks.popover.viewAll")}
          </Button>
        </div>
      </Popover>
    </div>
  );
}
